<template>
  <div
    class="dossier-item"
    :class="{
      'is-top-level': toplevel,
      'is-displayed': displayed,
    }"
    :data-path="arrayPath"
  >
    <!-- Handle direct array items -->
    <template v-if="schema.items">
      <!-- Show all array items if we have them -->
      <template v-if="arrayItems && arrayItems.length > 0">
        <div
          v-for="(item, index) in arrayItems"
          :key="index"
          class="array-item"
          :data-array-index="index"
          :data-array-path="arrayPath"
        >
          <!-- <div class="array-item-header">
            Item {{ index + 1 }}
          </div> -->
          <DossierItem
            :id="schema.id"
            :schema="schema.items"
            :ids="updatedIds"
            :depth="depth"
            :array-index="index"
            is-repeatable-item
            @navigate-array="$emit('navigate-array', $event)"
            @field-changed="() => emit('field-changed')"
          />
        </div>

        <!-- Only show add button when we already have items -->
        <div class="array-controls">
          <Button variant="primary" @click="addArrayItem"> Add an item </Button>
        </div>
      </template>
      <!-- If no items yet, show a simple add button -->
      <template v-else>
        <div class="array-controls">
          <Button variant="primary" @click="addArrayItem"> Add item </Button>
        </div>
      </template>
    </template>

    <!-- There's still nested properties -->
    <div
      v-else-if="hasNestedProperties"
      class="group"
      :class="{
        [`is-depth-${depth}`]: true,
        'is-parent-of-object': isParentOfObject,
        'is-parent-of-array': isParentOfArray,
        'is-repeatable-item': isRepeatableItem,
      }"
      :data-depth="depth"
      :data-id="id"
      :style="{ '--depth': depth - 1 }"
    >
      <div v-if="depth > 0" class="group-heading">
        <span v-if="parentTitle" class="group-title">{{ parentTitle }}</span>
        <span v-if="parentDescription" class="group-description">{{
          parentDescription
        }}</span>
        <span
          v-if="shouldShowParentLabels"
          class="group-title"
          :class="{ 'ordinal-title': isRepeatableItem }"
          :style="
            isRepeatableItem
              ? { backgroundColor: getBackgroundColor(arrayIndex) }
              : {}
          "
          >{{ isRepeatableItem ? `${numberToOrdinalText(arrayIndex + 1)} ` : ""
          }}{{ properTitle }}</span
        >
        <span v-if="schema.description" class="group-description">{{
          schema.description
        }}</span>
        <div
          v-if="isParentOfArray"
          class="hide-for-print add-item-button tw-text-sm tw-flex tw-items-center stk-color-primary-50 tw-whitespace-nowrap tw-cursor-pointer"
          @click="addArrayItem"
        >
          Add an item
        </div>
        <div
          v-if="isRepeatableItem"
          class="hide-for-print add-item-button tw-text-sm tw-flex tw-items-center tw-text-red-500 tw-whitespace-nowrap tw-cursor-pointer"
          @click="deleteArrayItem"
        >
          Delete this item
        </div>
      </div>

      <div class="group-content">
        <template v-if="schema.anyOf">
          <DossierItem
            v-for="(element, name) in schema.anyOf"
            :id="name"
            :key="name"
            :schema="element"
            :ids="updatedIds"
            :depth="isParentOfObject && depth > 0 ? depth : depth + 1"
            :array-index="props.arrayIndex"
            :parent-title="passParentTitle"
            :parent-description="passParentDescription"
            @navigate-array="$emit('navigate-array', $event)"
            @field-changed="() => emit('field-changed')"
          />
        </template>

        <template v-else-if="schema.properties">
          <div class="properties-grid">
            <template v-for="(property, name) in schema.properties">
              <div
                :key="name"
                class="grid-item tw-flex tw-flex-col tw-justify-end"
                :class="{
                  'full-width':
                    property.properties ||
                    property.items ||
                    (property.anyOf &&
                      (property.anyOf.some((p) => p.properties) ||
                        property.anyOf.some((p) => p.items))),
                }"
              >
                <DossierItem
                  :id="name"
                  :schema="property"
                  :ids="updatedIds"
                  :depth="depth + 1"
                  :array-index="props.arrayIndex"
                  :parent-title="passParentTitle"
                  :parent-description="passParentDescription"
                  @navigate-array="$emit('navigate-array', $event)"
                  @field-changed="() => emit('field-changed')"
                />
              </div>
            </template>
          </div>
        </template>
      </div>
    </div>

    <!-- We're at the last level -->
    <DossierField
      v-else-if="schema.anyOf"
      :id="id"
      :property="schema"
      :ids="updatedIds"
      :array-index="props.arrayIndex"
      @field-changed="() => emit('field-changed')"
    />
  </div>
</template>

<script setup>
import { Button } from "@stakeui";
import { computed, nextTick } from "vue";
import { prettifyMixedCase } from "../../../../shared/Util";
import { useDossierStore } from "../../../stores/dossierStore";
import DossierField from "./DossierField.vue";

const dossierStore = useDossierStore();

// Function to get background color based on index
function getBackgroundColor(index) {
  const colors = ["#d1e0fe", "#e5c2e1", "#c7f9de"];
  return colors[index % colors.length];
}

// Function to convert numbers to ordinal text
function numberToOrdinalText(num) {
  const ordinals = {
    1: "First",
    2: "Second",
    3: "Third",
    4: "Fourth",
    5: "Fifth",
    6: "Sixth",
    7: "Seventh",
    8: "Eighth",
    9: "Ninth",
    10: "Tenth",
    11: "Eleventh",
    12: "Twelfth",
    13: "Thirteenth",
    14: "Fourteenth",
    15: "Fifteenth",
    16: "Sixteenth",
    17: "Seventeenth",
    18: "Eighteenth",
    19: "Nineteenth",
    20: "Twentieth",
  };

  return ordinals[num] || `${num}${getOrdinalSuffix(num)}`;
}

// Helper function to get ordinal suffix for numbers not in the mapping
function getOrdinalSuffix(num) {
  const j = num % 10;
  const k = num % 100;

  if (j === 1 && k !== 11) {
    return "st";
  }
  if (j === 2 && k !== 12) {
    return "nd";
  }
  if (j === 3 && k !== 13) {
    return "rd";
  }

  return "th";
}

const props = defineProps({
  id: [String, Number],
  ids: Array,
  schema: Object,
  depth: Number,
  isRepeatableItem: Boolean,
  arrayIndex: {
    type: Number,
    default: 0,
  },
  parentTitle: String,
  parentDescription: String,
  toplevel: {
    type: Boolean,
    default: false,
  },
  displayed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["navigate-array", "field-changed"]);

const updatedIds = computed(() => {
  if (props.id && typeof props.id === "string") {
    return [...props.ids, props.id];
  }

  return props.ids;
});

const properTitle = computed(() => {
  // Use the id or the schema title
  if ((!props.id || typeof props.id !== "string") && !props.schema.title) {
    return null;
  }

  const sourceText = props.schema.title || props.id;

  return prettifyMixedCase(sourceText);
});

const isParentOfObject = computed(() => {
  if (!props.schema.anyOf) {
    return false;
  }

  // Get all the options that are not null
  const validOptions = props.schema.anyOf.filter(
    (option) => option.type !== "null"
  );

  return validOptions.every((item) => item.hasOwnProperty("properties"));
});

const isParentOfArray = computed(() => {
  if (!props.schema.anyOf) {
    return false;
  }

  // Get all the options that are not null
  const validOptions = props.schema.anyOf.filter(
    (option) => option.type !== "null"
  );

  // Check if any valid option has an 'items' property, indicating it's an array
  return validOptions.some((item) => item.hasOwnProperty("items"));
});

const hasNestedProperties = computed(() => {
  return (
    (props.schema.anyOf &&
      props.schema.anyOf.some(
        (element) =>
          element.hasOwnProperty("items") ||
          element.hasOwnProperty("properties")
      )) ||
    props.schema.hasOwnProperty("properties")
  );
});

// Add this computed property to control when to show title/description in the parent
const shouldShowParentLabels = computed(() => {
  return props.isRepeatableItem || isParentOfArray.value;
});

// Get current array items
const currentArrayItems = computed(() => {
  if (!isParentOfArray.value) return null;

  // Get the current value at this path
  let current = dossierStore.currentValue;
  for (const key of updatedIds.value) {
    if (!current) return null;
    current = current[key];
  }

  // If it's an array, return it
  return Array.isArray(current) ? current : null;
});

// For direct array schema
const arrayItems = computed(() => {
  if (!props.schema.items) return null;

  // Get the current value at this path
  let current = dossierStore.currentValue;
  for (const key of updatedIds.value) {
    if (!current) return null;

    // Check if we're at an array in the path
    if (Array.isArray(current)) {
      // If we have items in the array, access by index
      if (current.length > props.arrayIndex) {
        current = current[props.arrayIndex];
      } else {
        return null;
      }
    }

    current = current[key];
  }

  // If it's an array, return it
  return Array.isArray(current) ? current : null;
});

const passParentTitle = computed(() => {
  if (isParentOfObject.value) {
    return properTitle.value;
  }

  return null;
});

const passParentDescription = computed(() => {
  if (isParentOfObject.value && props.schema.description) {
    return props.schema.description;
  }

  return null;
});

// Add a unique path identifier for the array
const arrayPath = computed(() => {
  return (
    updatedIds.value.join("-") +
    (props.arrayIndex !== undefined ? `-${props.arrayIndex}` : "")
  );
});

// Function to get the schema for array items
function getArrayItemSchema() {
  if (!isParentOfArray.value) return null;

  // Find the array schema from anyOf options
  const validOptions = props.schema.anyOf.filter(
    (option) => option.type !== "null"
  );
  const arraySchema = validOptions.find((item) => item.hasOwnProperty("items"));

  if (arraySchema && arraySchema.items) {
    return arraySchema.items;
  }

  return null;
}

// Function to create an empty item from schema
function createEmptyItemFromSchema(schema) {
  if (!schema) return {};

  // For simple types
  if (schema.type === "string") return "";
  if (schema.type === "number" || schema.type === "integer") return null;
  if (schema.type === "boolean") return false;

  // For objects with properties
  if (schema.properties) {
    const result = {};
    Object.keys(schema.properties).forEach((key) => {
      result[key] = createEmptyItemFromSchema(schema.properties[key]);
    });
    return result;
  }

  // For arrays
  if (schema.items) {
    return [];
  }

  // For anyOf/allOf/oneOf
  if (schema.anyOf) {
    const nonNullSchema = schema.anyOf.find((s) => s.type !== "null");
    if (nonNullSchema) {
      return createEmptyItemFromSchema(nonNullSchema);
    }
  }

  return {};
}

// Function to add a new array item
function addArrayItem() {
  // Set edit mode when adding an item
  dossierStore.setEditMode(true);

  // Start path navigation at the store's current value
  let current = dossierStore.currentValue;

  // Navigate to the correct position in the object using updatedIds
  // Create the path if it doesn't exist
  const path = [...updatedIds.value];

  // Create the object path if it doesn't exist
  for (let i = 0; i < path.length; i++) {
    const key = path[i];

    // If we're at the end, create/ensure it's an array
    if (i === path.length - 1) {
      if (!current[key] || !Array.isArray(current[key])) {
        // Initialize as empty array if not defined or not an array
        current[key] = [];
      }
    } else {
      // Create intermediate object if it doesn't exist
      if (!current[key]) {
        current[key] = {};
      }

      // Move to the next level
      current = current[key];

      // If we're at an array in the path, handle array index
      if (Array.isArray(current)) {
        // If we have items in the array, access by index
        if (current.length > props.arrayIndex) {
          current = current[props.arrayIndex];
        } else {
          // Extend array if needed
          while (current.length <= props.arrayIndex) {
            current.push({});
          }
          current = current[props.arrayIndex];
        }
      }
    }
  }

  // Get a reference to the array we want to modify
  const targetArray =
    path.length > 0
      ? path.reduce((obj, key) => {
          if (Array.isArray(obj)) {
            if (obj.length > props.arrayIndex) {
              return obj[props.arrayIndex][key];
            }
            return null;
          }
          return obj[key];
        }, dossierStore.currentValue)
      : dossierStore.currentValue;

  if (!targetArray || !Array.isArray(targetArray)) {
    console.error("Target array not found or not an array");
    return;
  }

  // Create a template for the new item based on schema
  const newItem = createEmptyItemFromSchema(props.schema.items);

  // Add the new item to the array
  targetArray.push(newItem);

  // Force reactivity update
  dossierStore.setCurrentValue({ ...dossierStore.currentValue });

  // Wait for the DOM to update, then scroll to the newly added item
  nextTick(() => {
    try {
      // Get the current array path
      const path = arrayPath.value;

      // Find all array items with this specific path
      const arrayItems = document.querySelectorAll(
        `.array-item[data-array-path="${path}"]`
      );

      if (arrayItems.length > 0) {
        // Get the last array item (the one just added)
        const lastItem = arrayItems[arrayItems.length - 1];
        // Scroll to the new item with smooth behavior
        lastItem.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      // Fallback: Try to find the array items by their container
      const container = document.querySelector(
        `.dossier-item[data-path="${path}"]`
      );
      if (container) {
        const items = container.querySelectorAll(".array-item");
        if (items.length > 0) {
          items[items.length - 1].scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
          return;
        }
      }

      // Last resort fallback
      const allArrayItems = document.querySelectorAll(".array-item");
      if (allArrayItems.length > 0) {
        allArrayItems[allArrayItems.length - 1].scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    } catch (error) {
      console.error("Error scrolling to array item:", error);
    }
  });
}

// Function to delete an array item
function deleteArrayItem() {
  // Set edit mode when deleting an item
  dossierStore.setEditMode(true);

  // Start path navigation at the store's current value
  const current = dossierStore.currentValue;

  // Get path to the parent array
  const parentIds = [...props.ids];
  const arrayIndex = props.arrayIndex;

  if (parentIds.length === 0) {
    console.error("Cannot delete an item without a parent array path");
    return;
  }

  // Navigate to the parent array
  const parentArray = parentIds.reduce((obj, key) => obj?.[key], current);

  if (!parentArray || !Array.isArray(parentArray)) {
    console.error("Parent is not an array or does not exist");
    return;
  }

  // Remove the item at the specified index
  parentArray.splice(arrayIndex, 1);

  // Force reactivity update
  dossierStore.setCurrentValue({ ...dossierStore.currentValue });
}
</script>

<style scoped>
.dossier-item {
  --heading-height: 2.5rem;
  width: 100%;

  &.is-top-level:not(.is-displayed) {
    display: none;
  }
}

.group {
  width: 100%;
  margin-bottom: 1.5rem;
  transition-duration: var(--stk-transition-duration);
  transition-property: border-color;
  position: relative;
}

.group.is-parent-of-object {
  background: none;
  border: none;
  padding: 0;
}

.group.is-parent-of-object > .group-heading {
  display: none;
}

.group.view-mode .group-heading {
  left: 0;
}

.group-heading {
  display: flex;
  border-bottom: 1px solid var(--stk-text-90);
  margin-bottom: 1rem;
  min-height: var(--heading-height);
  position: sticky;
  top: calc((var(--depth)) * var(--heading-height));
  background-color: white;
  z-index: calc(30 - var(--depth));
  padding: 0.5rem 0;
  align-items: center;
  flex-wrap: nowrap;
}

.group-content {
  padding: 0;
  margin-bottom: 2rem;
}

.properties-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;
  align-items: start;
}

.properties-grid .full-width {
  grid-column: 1 / -1;
}

.group-description {
  display: flex;
  line-height: 1.2;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 0 1 auto; /* Allow shrinking but not growing */
  min-width: 0; /* Allow description to shrink below content size */
  color: var(--stk-text-60);
  font-size: calc(1rem - (var(--depth) * 0.05rem));
  margin-left: 0.5rem;
  white-space: nowrap; /* Prevent wrapping */
}

.group-title {
  display: flex;
  line-height: 1.2;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--stk-text-40);
  font-weight: 600;
  font-size: calc(1.5rem - (var(--depth) * 0.15rem));
  letter-spacing: calc(-0.01em * var(--depth));
  padding-right: 0.5rem;
  flex: 0 0 auto; /* Don't allow shrinking, take as much space as needed */
}

.ordinal-title {
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: calc(1.25rem - (var(--depth) * 0.15rem));
  color: var(--stk-text-40);
}

.add-item-button {
  margin-left: auto; /* Push to right side */
  flex: 0 0 auto; /* Don't allow shrinking */
}

.delete-item {
  margin-top: 0.5rem;
}

/* Top-level styles */
.group.is-depth-0 {
  background-color: transparent;
  border: none;
  padding: 0;
  margin-top: 0;
}

.group.is-depth-0 > .group-heading {
  display: none;
}

/* Array item styling */
.array-item {
  position: relative;
  padding: 1rem;
  padding-top: 0;
  margin-bottom: 1.5rem;
  border: 1px solid var(--stk-text-90);
  border-radius: 1rem;
}

.array-item-header {
  background-color: var(--stk-primary);
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  position: absolute;
  top: -0.75rem;
  left: 1rem;
}

.array-controls {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}
</style>
